<template>
  <div class="settings--content">
    <settings-section
      :title="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_CALLING.TITLE')"
      :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_CALLING.SUB_TITLE')"
    >
      <label class="medium-9 columns settings-item">
        <div class="enter-to-send--checkbox">
          <input
            id="enableCalling"
            v-model="callingEnabled"
            type="checkbox"
            :disabled="isTogglingCalling"
            @change="handleCallingToggle"
          />
          <label for="enableCalling">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_CALLING.ENABLE.LABEL') }}
          </label>
        </div>
        <p class="help-text">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_CALLING.ENABLE.HELP_TEXT') }}
        </p>
      </label>

      <label v-if="callingEnabled" class="medium-9 columns settings-item">
        <div class="enter-to-send--checkbox">
          <input
            id="enableInboundCalls"
            v-model="inboundCallsEnabled"
            type="checkbox"
            :disabled="isTogglingInbound"
            @change="handleInboundToggle"
          />
          <label for="enableInboundCalls">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_CALLING.INBOUND.LABEL') }}
          </label>
        </div>
        <p class="help-text">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_CALLING.INBOUND.HELP_TEXT') }}
        </p>
      </label>

      <label v-if="callingEnabled" class="medium-9 columns settings-item">
        <div class="enter-to-send--checkbox">
          <input
            id="enableCallRecording"
            v-model="callRecordingEnabled"
            type="checkbox"
            :disabled="isTogglingRecording"
            @change="handleRecordingToggle"
          />
          <label for="enableCallRecording">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_CALLING.RECORDING.LABEL') }}
          </label>
        </div>
        <p class="help-text">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_CALLING.RECORDING.HELP_TEXT') }}
        </p>
      </label>

      <label v-if="callingEnabled" class="medium-9 columns settings-item">
        <span class="input-group-title">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_CALLING.PERMISSION_REQUEST_BODY.LABEL') }}
        </span>
        <textarea
          id="call-permission-body"
          v-model="permissionRequestBody"
          class="permission-body"
          rows="3"
          :disabled="isSavingBody"
          :placeholder="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_CALLING.PERMISSION_REQUEST_BODY.PLACEHOLDER')"
          @blur="handleBodyChange"
        />
        <p class="help-text">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_CALLING.PERMISSION_REQUEST_BODY.HELP_TEXT') }}
        </p>
      </label>

      <label v-if="callingEnabled" class="medium-9 columns settings-item">
        <span class="input-group-title">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_CALLING.TEMPLATE.LABEL') }}
        </span>
        <select
          id="call-permission-template"
          v-model="selectedTemplateKey"
          class="template-select"
          :disabled="isSavingTemplate"
          @change="handleTemplateChange"
        >
          <option value="">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_CALLING.TEMPLATE.PLACEHOLDER') }}
          </option>
          <option
            v-for="template in callPermissionTemplates"
            :key="templateKey(template)"
            :value="templateKey(template)"
          >
            {{ template.name }} ({{ template.language }})
          </option>
        </select>
        <p class="help-text">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_CALLING.TEMPLATE.HELP_TEXT') }}
        </p>
      </label>
    </settings-section>
  </div>
</template>

<script>
import alertMixin from 'shared/mixins/alertMixin';
import InboxesAPI from '../../../../../api/inboxes';
import SettingsSection from '../../../../../components/SettingsSection';

export default {
  components: { SettingsSection },
  mixins: [alertMixin],
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      callingEnabled: this.inbox.provider_config?.calling_enabled !== false,
      inboundCallsEnabled: this.inbox.provider_config?.inbound_calls_enabled !== false,
      callRecordingEnabled: this.inbox.provider_config?.recording_enabled !== false,
      isTogglingCalling: false,
      isTogglingInbound: false,
      isTogglingRecording: false,
      isSavingTemplate: false,
      isSavingBody: false,
      permissionRequestBody:
        this.inbox.provider_config?.call_permission_request_body || '',
      selectedTemplateKey: this.storedTemplateKey(),
    };
  },
  computed: {
    callPermissionTemplates() {
      return (this.inbox.message_templates || []).filter(template => {
        const serializedTemplate = JSON.stringify(template).toUpperCase();
        return (
          template.status?.toUpperCase() === 'APPROVED' &&
          serializedTemplate.includes('CALL_PERMISSION_REQUEST')
        );
      });
    },
  },
  watch: {
    'inbox.provider_config.calling_enabled'(val) {
      this.callingEnabled = val !== false;
    },
    'inbox.provider_config.inbound_calls_enabled'(val) {
      this.inboundCallsEnabled = val !== false;
    },
    'inbox.provider_config.call_permission_template': {
      deep: true,
      handler() {
        this.selectedTemplateKey = this.storedTemplateKey();
      },
    },
    inbox: {
      immediate: true,
      handler(newInbox) {
        this.callRecordingEnabled = newInbox?.provider_config?.recording_enabled !== false;
      },
    },
  },
  methods: {
    templateKey(template) {
      return `${template.name}|${template.language}`;
    },
    storedTemplateKey() {
      const template = this.inbox.provider_config?.call_permission_template;
      return template?.name && template?.language
        ? `${template.name}|${template.language}`
        : '';
    },
    async handleBodyChange() {
      const storedBody = this.inbox.provider_config?.call_permission_request_body || '';
      if (this.permissionRequestBody === storedBody) return;

      this.isSavingBody = true;
      try {
        await InboxesAPI.updateProviderConfig(this.inbox.id, {
          call_permission_request_body: this.permissionRequestBody,
        });
        await this.refreshInbox();
        this.showAlert(this.$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_CALLING.API.SUCCESS_MESSAGE'));
      } catch (error) {
        this.permissionRequestBody = storedBody;
        this.showAlert(
          error.response?.data?.error ||
            this.$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_CALLING.API.ERROR_MESSAGE')
        );
      } finally {
        this.isSavingBody = false;
      }
    },
    async handleTemplateChange() {
      const previousValue = this.storedTemplateKey();
      const template = this.callPermissionTemplates.find(
        item => this.templateKey(item) === this.selectedTemplateKey
      );
      this.isSavingTemplate = true;
      try {
        await InboxesAPI.updateProviderConfig(this.inbox.id, {
          call_permission_template: template
            ? { name: template.name, language: template.language }
            : null,
        });
        await this.refreshInbox();
        this.showAlert(this.$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_CALLING.API.SUCCESS_MESSAGE'));
      } catch (error) {
        this.selectedTemplateKey = previousValue;
        this.showAlert(
          error.response?.data?.error ||
            this.$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_CALLING.API.ERROR_MESSAGE')
        );
      } finally {
        this.isSavingTemplate = false;
      }
    },
    async handleCallingToggle() {
      const previousValue = this.inbox.provider_config?.calling_enabled !== false;
      this.isTogglingCalling = true;
      try {
        if (this.callingEnabled) {
          await InboxesAPI.enableWhatsappCalling(this.inbox.id);
        } else {
          await InboxesAPI.disableWhatsappCalling(this.inbox.id);
        }
        await this.refreshInbox();
        this.showAlert(this.$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_CALLING.API.SUCCESS_MESSAGE'));
      } catch (error) {
        this.callingEnabled = previousValue;
        this.showAlert(
          error.response?.data?.error ||
            this.$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_CALLING.API.ERROR_MESSAGE')
        );
      } finally {
        this.isTogglingCalling = false;
      }
    },
    async handleInboundToggle() {
      const previousValue = this.inbox.provider_config?.inbound_calls_enabled !== false;
      this.isTogglingInbound = true;
      try {
        await InboxesAPI.setInboundCalls(this.inbox.id, this.inboundCallsEnabled);
        await this.refreshInbox();
        this.showAlert(this.$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_CALLING.API.SUCCESS_MESSAGE'));
      } catch (error) {
        this.inboundCallsEnabled = previousValue;
        this.showAlert(
          error.response?.data?.error ||
            this.$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_CALLING.API.ERROR_MESSAGE')
        );
      } finally {
        this.isTogglingInbound = false;
      }
    },
    async handleRecordingToggle() {
      const previousValue = this.inbox.provider_config?.recording_enabled !== false;
      this.isTogglingRecording = true;
      try {
        await InboxesAPI.setCallRecording(this.inbox.id, this.callRecordingEnabled);
        await this.refreshInbox();
        this.showAlert(this.$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_CALLING.API.SUCCESS_MESSAGE'));
      } catch (error) {
        this.callRecordingEnabled = previousValue;
        this.showAlert(
          error.response?.data?.error ||
            this.$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_CALLING.API.ERROR_MESSAGE')
        );
      } finally {
        this.isTogglingRecording = false;
      }
    },
    refreshInbox() {
      return this.$store.dispatch('inboxes/get', this.inbox.id);
    },
  },
};
</script>

<style scoped lang="scss">
@import '~dashboard/assets/scss/woot';

.settings-item {
  padding-bottom: var(--space-normal);
}

.input-group-title {
  display: block;
  margin-bottom: var(--space-smaller);
}

.template-select,
.permission-body {
  margin: 0;
  width: 100%;
}

.permission-body {
  resize: vertical;
}

.help-text {
  color: var(--s-600);
  font-size: var(--font-size-small);
  font-style: italic;
  margin: 0;
  margin-top: var(--space-smaller);
}
</style>
